import React, { type FC, useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';

import { useObjectSidebarActions } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';

import { createAgentTrustReceiptPanel } from './createAgentTrustReceiptPanel.tsx';
import type { AgentTrustReceipt } from './agentTrustReceiptModel.ts';
import { resolveAgentTrustReceipt } from './resolveAgentTrustReceipt.ts';

const i18n = defineMessages({
	viewReceiptAction: {
		id: 'agent-trust-receipt.byline-chip.view-receipt-action.non-final',
		defaultMessage: 'View receipt',
		description: 'Action text in the byline button that opens the Agent Trust Receipt panel.',
	},
});

type AgentTrustReceiptBylineChipProps = {
	contentId: string;
};

type AgentTrustReceiptBylineChipButtonProps = {
	receipt: AgentTrustReceipt;
};

const AgentTrustReceiptBylineChipButton: FC<AgentTrustReceiptBylineChipButtonProps> = ({
	receipt,
}) => {
	const { formatMessage } = useIntl();
	const { showObjectSidebar } = useObjectSidebarActions();
	const chipLabel = `${receipt.scenario.label} - ${formatMessage(i18n.viewReceiptAction)}`;

	const openReceiptPanel = useCallback(() => {
		showObjectSidebar(createAgentTrustReceiptPanel({ receipt }), 'push');
	}, [receipt, showObjectSidebar]);

	return (
		<Button
			appearance="subtle"
			spacing="compact"
			iconBefore={AiAgentIcon}
			testId="agent-trust-receipt-byline-chip"
			onClick={openReceiptPanel}
		>
			{chipLabel}
		</Button>
	);
};

const AgentTrustReceiptBylineChipInner: FC<AgentTrustReceiptBylineChipProps> = ({ contentId }) => {
	const receipt = useMemo(() => resolveAgentTrustReceipt(contentId), [contentId]);

	if (!receipt) {
		return null;
	}

	return <AgentTrustReceiptBylineChipButton receipt={receipt} />;
};

export const AgentTrustReceiptBylineChip: React.FC<AgentTrustReceiptBylineChipProps> =
	AgentTrustReceiptBylineChipInner;
